import React from 'react';
import { useParams, Link } from 'react-router-dom';
import './BlogDetailPage.css';

function BlogDetailPage() {
  const { slug } = useParams();

  // Blog posts data - in a real app, this would come from an API or data file
  const blogPosts = {
    'great-wildebeest-migration': {
      id: 1,
      title: 'The Great Wildebeest Migration: A Natural Wonder',
      image: '/assets/imgs/destination/serengeti1.jpg',
      category: 'Wildlife',
      author: 'Digital Dream Safaris',
      date: '2024-01-15',
      readTime: '5 min read',
      content: `
        <p>The Great Wildebeest Migration is one of the most spectacular natural events on Earth. Every year, over 1.5 million wildebeest, along with hundreds of thousands of zebras and gazelles, embark on a circular journey across the Serengeti ecosystem in Tanzania and Kenya.</p>
        
        <h2>The Migration Cycle</h2>
        <p>The migration follows a predictable pattern, driven by the search for fresh grazing and water. The journey begins in the southern Serengeti and Ngorongoro Conservation Area, where the wildebeest give birth to their calves between January and March.</p>
        
        <p>As the dry season approaches, the herds move northward through the Serengeti, following the rains. By June and July, they reach the Grumeti River, where they face their first major obstacle - crocodile-infested waters.</p>
        
        <h2>The River Crossings</h2>
        <p>The most dramatic part of the migration occurs at the Mara River crossings in northern Serengeti and the Masai Mara. Here, thousands of wildebeest gather on the riverbanks, building up courage before plunging into the crocodile-filled waters.</p>
        
        <p>These crossings are truly spectacular to witness. The wildebeest surge across the river in massive groups, creating a chaotic and thrilling scene. While many make it across safely, some fall victim to crocodiles or get trampled in the chaos.</p>
        
        <h2>Best Time to Witness</h2>
        <p>The best time to witness the migration depends on which part of the journey you want to see:</p>
        <ul>
          <li><strong>Calving Season (January-March):</strong> Witness thousands of calves being born in the southern Serengeti</li>
          <li><strong>River Crossings (July-October):</strong> See the dramatic Mara River crossings in northern Serengeti</li>
          <li><strong>Return Journey (November-December):</strong> Watch the herds return south as the short rains begin</li>
        </ul>
        
        <h2>Why It Matters</h2>
        <p>This migration is crucial for the ecosystem. The wildebeest help maintain the grasslands by grazing, and their movement distributes nutrients across vast areas. The migration also supports a diverse range of predators, including lions, cheetahs, hyenas, and crocodiles.</p>
        
        <p>Witnessing the Great Migration is a once-in-a-lifetime experience that showcases the raw power and beauty of nature. It's a testament to the incredible resilience and instinct of these animals as they follow ancient paths across the African plains.</p>
      `
    },
    'first-safari-tips': {
      id: 2,
      title: 'Top 10 Tips for Your First Safari in Tanzania',
      image: '/assets/imgs/destination/ngorongoro1.jpg',
      category: 'Travel Tips',
      author: 'Digital Dream Safaris',
      date: '2024-01-10',
      readTime: '7 min read',
      content: `
        <p>Planning your first safari in Tanzania? Congratulations! You're about to embark on an incredible adventure. Here are our top 10 tips to ensure your safari experience is unforgettable.</p>
        
        <h2>1. Choose the Right Time to Visit</h2>
        <p>The dry season (June to October) offers the best wildlife viewing as animals gather around water sources. However, the wet season (November to May) brings lush landscapes and fewer crowds, plus the chance to witness the Great Migration calving season.</p>
        
        <h2>2. Pack Smart</h2>
        <p>Pack neutral-colored clothing (khaki, beige, olive green) to blend in with the environment. Avoid bright colors and white, which can startle animals. Don't forget:</p>
        <ul>
          <li>Binoculars for wildlife viewing</li>
          <li>Camera with extra batteries and memory cards</li>
          <li>Sunscreen and hat</li>
          <li>Insect repellent</li>
          <li>Layers for early morning game drives</li>
        </ul>
        
        <h2>3. Be Patient</h2>
        <p>Wildlife viewing requires patience. Animals don't appear on schedule, so enjoy the journey and the beautiful landscapes. Sometimes the most memorable moments happen when you least expect them.</p>
        
        <h2>4. Listen to Your Guide</h2>
        <p>Your safari guide is an expert with years of experience. Listen to their instructions, especially regarding safety. They know where to find animals and how to position the vehicle for the best viewing.</p>
        
        <h2>5. Respect Wildlife</h2>
        <p>Always maintain a safe distance from animals. Never attempt to feed or touch wildlife. Keep noise levels low and avoid sudden movements that might startle the animals.</p>
        
        <h2>6. Early Mornings Are Golden</h2>
        <p>Animals are most active during the early morning and late afternoon. Be prepared for early wake-up calls - it's worth it for the incredible wildlife sightings and beautiful light for photography.</p>
        
        <h2>7. Stay Hydrated</h2>
        <p>The African sun can be intense. Drink plenty of water throughout the day, especially during game drives. Most safari vehicles carry water, but bring your own bottle as well.</p>
        
        <h2>8. Bring Motion Sickness Medication</h2>
        <p>Safari vehicles travel on bumpy roads. If you're prone to motion sickness, bring appropriate medication. The roads can be rough, especially in the rainy season.</p>
        
        <h2>9. Manage Expectations</h2>
        <p>While safaris offer incredible wildlife viewing, there are no guarantees. Some days you might see the Big Five, other days might be quieter. Enjoy every moment, including the beautiful landscapes and birdlife.</p>
        
        <h2>10. Disconnect and Immerse</h2>
        <p>Take time to put your camera down and simply observe. The sounds, smells, and sights of the African bush are as much a part of the experience as the photos you'll take home.</p>
        
        <p>Remember, a safari is about connecting with nature and experiencing the wild in its purest form. Follow these tips, and you're sure to have an amazing adventure!</p>
      `
    },
    'climbing-kilimanjaro-guide': {
      id: 3,
      title: 'Climbing Mount Kilimanjaro: Everything You Need to Know',
      image: '/assets/imgs/destination/Mount-Kilimanjaro.jpg',
      category: 'Adventure',
      author: 'Digital Dream Safaris',
      date: '2024-01-05',
      readTime: '10 min read',
      content: `
        <p>Mount Kilimanjaro, Africa's highest peak at 5,895 meters (19,341 feet), is one of the world's most accessible high-altitude climbs. Unlike technical mountaineering peaks, Kilimanjaro can be climbed by fit hikers with proper preparation.</p>
        
        <h2>Choosing Your Route</h2>
        <p>Kilimanjaro offers several routes, each with different characteristics:</p>
        <ul>
          <li><strong>Marangu Route:</strong> Known as the "Coca-Cola Route," this is the easiest and most popular path with hut accommodations</li>
          <li><strong>Machame Route:</strong> The "Whiskey Route" offers better scenery and acclimatization, but is more challenging</li>
          <li><strong>Lemosho Route:</strong> One of the most scenic routes with excellent acclimatization opportunities</li>
          <li><strong>Rongai Route:</strong> The only route approaching from the north, offering a unique perspective</li>
        </ul>
        
        <h2>Physical Preparation</h2>
        <p>While Kilimanjaro doesn't require technical climbing skills, it does demand good physical fitness. Start training at least 3-4 months before your climb:</p>
        <ul>
          <li>Regular cardiovascular exercise (running, cycling, swimming)</li>
          <li>Hiking with a weighted backpack</li>
          <li>Stair climbing or hill walking</li>
          <li>Strength training for legs and core</li>
        </ul>
        
        <h2>Altitude Acclimatization</h2>
        <p>Altitude sickness is the biggest challenge on Kilimanjaro. Choose a route that allows for proper acclimatization - typically 7-9 days. The key is to "climb high, sleep low" - gain altitude during the day but sleep at lower elevations.</p>
        
        <h2>What to Pack</h2>
        <p>Proper gear is essential for a successful climb:</p>
        <ul>
          <li>Layered clothing system (base layer, insulating layer, outer shell)</li>
          <li>Quality hiking boots (broken in before the climb)</li>
          <li>Sleeping bag rated for cold temperatures</li>
          <li>Headlamp with extra batteries</li>
          <li>Trekking poles</li>
          <li>Water bottles or hydration system</li>
        </ul>
        
        <h2>The Summit Day</h2>
        <p>Summit day is the most challenging part of the climb. You'll start around midnight, climbing in the dark with headlamps. The final ascent is steep and can take 6-8 hours. Reaching the summit at sunrise is an unforgettable experience.</p>
        
        <h2>Success Tips</h2>
        <p>Follow the "pole pole" (slowly, slowly) philosophy - your guides will set a pace that allows for proper acclimatization. Stay hydrated, eat regularly, and listen to your body. Most importantly, choose a reputable tour operator with experienced guides.</p>
        
        <p>Climbing Kilimanjaro is a challenging but achievable goal. With proper preparation, the right gear, and a good guide, you can stand on the Roof of Africa!</p>
      `
    },
    'big-five-ngorongoro': {
      id: 4,
      title: 'Discovering the Big Five in Ngorongoro Crater',
      image: '/assets/imgs/destination/ngorongoro.webp',
      category: 'Safari',
      author: 'Digital Dream Safaris',
      date: '2023-12-28',
      readTime: '6 min read',
      content: `
        <p>The Ngorongoro Crater is one of Africa's most unique safari destinations. This UNESCO World Heritage Site is the world's largest intact volcanic caldera, creating a natural enclosure that supports an incredible density of wildlife.</p>
        
        <h2>What Makes Ngorongoro Special</h2>
        <p>Formed over two million years ago when a massive volcano collapsed, the crater is 610 meters deep and covers 260 square kilometers. The crater floor provides year-round water and grazing, making it a haven for wildlife.</p>
        
        <h2>The Big Five</h2>
        <p>The Ngorongoro Crater is one of the best places in Africa to see all members of the Big Five in a single day:</p>
        <ul>
          <li><strong>Lions:</strong> The crater supports a healthy population of lions, often seen resting or hunting</li>
          <li><strong>Elephants:</strong> Large bull elephants roam the crater floor, though breeding herds are less common</li>
          <li><strong>Buffalo:</strong> Massive herds of buffalo graze on the crater floor</li>
          <li><strong>Rhinos:</strong> The crater is home to critically endangered black rhinos, one of the best places to see them</li>
          <li><strong>Leopards:</strong> While more elusive, leopards can be spotted in the crater's forested areas</li>
        </ul>
        
        <h2>Other Wildlife</h2>
        <p>Beyond the Big Five, the crater is home to an incredible diversity of animals including hippos, hyenas, zebras, wildebeest, gazelles, and over 500 bird species. The Lerai Forest provides habitat for elephants and various primates.</p>
        
        <h2>Lake Magadi</h2>
        <p>The shallow alkaline Lake Magadi in the southwestern corner is a major attraction, hosting thousands of flamingos and other water birds. Hippos can often be seen wallowing in the lake's edges.</p>
        
        <h2>Best Time to Visit</h2>
        <p>The crater can be visited year-round, but the dry season (June to October) offers the best wildlife viewing as animals gather around water sources. The wet season brings lush green landscapes and fewer crowds.</p>
        
        <p>A visit to Ngorongoro Crater is a highlight of any Tanzanian safari, offering unparalleled opportunities to see Africa's most iconic wildlife in a stunning natural setting.</p>
      `
    },
    'zanzibar-tropical-paradise': {
      id: 5,
      title: 'Zanzibar: A Tropical Paradise Beyond the Beaches',
      image: '/assets/imgs/destination/zanzibar.jpeg',
      category: 'Culture',
      author: 'Digital Dream Safaris',
      date: '2023-12-20',
      readTime: '8 min read',
      content: `
        <p>Zanzibar is often thought of as just a beach destination, but this Spice Island offers so much more. From its rich history to vibrant culture and incredible marine life, Zanzibar is a destination that captivates all who visit.</p>
        
        <h2>Stone Town: A Living Museum</h2>
        <p>Stone Town, Zanzibar's historic heart, is a UNESCO World Heritage Site that tells the story of centuries of trade and cultural exchange. Wander through narrow alleys lined with ancient buildings featuring intricate wooden doors, visit the former slave market, and explore the Sultan's Palace.</p>
        
        <h2>The Spice Trade</h2>
        <p>Zanzibar earned its nickname "Spice Island" through its historic spice trade. Take a spice tour to see cloves, cinnamon, nutmeg, and vanilla growing, and learn about their uses in cooking and traditional medicine.</p>
        
        <h2>Pristine Beaches</h2>
        <p>Of course, Zanzibar's beaches are spectacular. The island's east coast offers long stretches of white sand and turquoise waters, perfect for relaxation. Nungwi and Kendwa in the north are popular for their beautiful sunsets.</p>
        
        <h2>Marine Adventures</h2>
        <p>The waters around Zanzibar are perfect for diving and snorkeling. The coral reefs are home to diverse marine life, and you might even spot dolphins or whale sharks. The Safari Blue tour offers a full day of snorkeling and beach activities.</p>
        
        <h2>Swahili Culture</h2>
        <p>Experience authentic Swahili culture through local cuisine, music, and traditions. Try Zanzibari dishes like pilau rice, biryani, and fresh seafood. The island's blend of African, Arab, and Indian influences creates a unique cultural tapestry.</p>
        
        <h2>Prison Island</h2>
        <p>Just off Stone Town, Prison Island (Changuu Island) is home to giant Aldabra tortoises. The island also offers beautiful beaches and excellent snorkeling opportunities.</p>
        
        <p>Zanzibar is the perfect complement to a Tanzanian safari, offering relaxation, culture, and adventure in equal measure. Whether you're interested in history, beaches, or marine life, Zanzibar has something for everyone.</p>
      `
    },
    'tarangire-elephant-paradise': {
      id: 6,
      title: 'Tarangire National Park: The Elephant Paradise',
      image: '/assets/imgs/destination/tarangire1.jpg',
      category: 'Wildlife',
      author: 'Digital Dream Safaris',
      date: '2023-12-15',
      readTime: '5 min read',
      content: `
        <p>Tarangire National Park is often called the "elephant paradise" of Tanzania, and for good reason. This beautiful park is home to some of the largest elephant herds in East Africa, making it a must-visit destination for wildlife enthusiasts.</p>
        
        <h2>Elephant Herds</h2>
        <p>During the dry season (June to October), Tarangire hosts thousands of elephants. You'll see herds numbering in the hundreds, with elephants of all ages - from tiny calves to massive bulls with impressive tusks. The Tarangire River provides a vital water source, attracting these magnificent animals.</p>
        
        <h2>Iconic Baobab Trees</h2>
        <p>The park's landscape is dominated by ancient baobab trees, some over a thousand years old. These massive trees create a unique and photogenic landscape, especially at sunset when they cast dramatic shadows across the plains.</p>
        
        <h2>Diverse Wildlife</h2>
        <p>While elephants are the star attraction, Tarangire is home to a wide variety of wildlife including lions, leopards, cheetahs, giraffes, zebras, wildebeest, and over 550 bird species. The park is particularly known for its large populations of impalas and dik-diks.</p>
        
        <h2>Seasonal Changes</h2>
        <p>Tarangire's character changes dramatically with the seasons. During the dry season, the park is packed with wildlife drawn to the permanent water sources. In the wet season, animals disperse across the park, but the landscape becomes lush and green.</p>
        
        <h2>Bird Watching Paradise</h2>
        <p>With over 550 bird species recorded, Tarangire is a birdwatcher's paradise. Look for colorful species like yellow-collared lovebirds, ashy starlings, and various hornbills. The park is also home to many raptors and water birds.</p>
        
        <p>Tarangire National Park offers an authentic safari experience with incredible elephant viewing and stunning landscapes. It's the perfect addition to any northern circuit safari itinerary.</p>
      `
    },
    'best-time-visit-tanzania': {
      id: 7,
      title: 'Best Time to Visit Tanzania: A Seasonal Guide',
      image: '/assets/imgs/destination/manyara.jpg',
      category: 'Travel Tips',
      author: 'Digital Dream Safaris',
      date: '2023-12-10',
      readTime: '6 min read',
      content: `
        <p>Understanding Tanzania's seasons is crucial for planning the perfect safari or adventure trip. The country experiences distinct wet and dry seasons that dramatically affect wildlife viewing, landscapes, and travel conditions.</p>
        
        <h2>Dry Season (June - October)</h2>
        <p>This is considered the best time for wildlife viewing. The weather is dry and sunny, and animals gather around permanent water sources, making them easier to spot. The vegetation is sparse, improving visibility.</p>
        <p><strong>Highlights:</strong> Excellent wildlife viewing, clear skies, comfortable temperatures, Great Migration river crossings (July-October)</p>
        
        <h2>Short Rains (November - December)</h2>
        <p>The short rains bring fresh green vegetation and fewer crowds. Wildlife is still abundant, and the landscapes are beautiful. This is a great time to visit if you want to avoid peak season crowds.</p>
        <p><strong>Highlights:</strong> Lush landscapes, fewer tourists, good wildlife viewing, Great Migration returns south</p>
        
        <h2>Long Rains (March - May)</h2>
        <p>The long rainy season brings heavy downpours, but also the Great Migration calving season in the southern Serengeti. Some lodges may close, but those that remain open offer excellent value.</p>
        <p><strong>Highlights:</strong> Calving season, fewer crowds, lower prices, lush green landscapes</p>
        
        <h2>Dry Season (January - February)</h2>
        <p>A short dry period between the rains, offering good wildlife viewing and pleasant weather. This is when the Great Migration is in the southern Serengeti, with thousands of calves being born.</p>
        <p><strong>Highlights:</strong> Calving season, good weather, excellent wildlife viewing</p>
        
        <h2>Consider Your Priorities</h2>
        <p>Choose your travel time based on what matters most to you:</p>
        <ul>
          <li><strong>Best Wildlife Viewing:</strong> June - October</li>
          <li><strong>Witness Calving Season:</strong> January - March</li>
          <li><strong>See River Crossings:</strong> July - October</li>
          <li><strong>Avoid Crowds:</strong> November - December, March - May</li>
          <li><strong>Best Value:</strong> March - May (long rains)</li>
        </ul>
        
        <p>No matter when you visit, Tanzania offers incredible wildlife experiences year-round. Each season has its own unique charm and advantages.</p>
      `
    },
    'maasai-culture-experience': {
      id: 8,
      title: 'Maasai Culture: Experiencing Traditional Tanzania',
      image: '/assets/imgs/maasai.jpg',
      category: 'Culture',
      author: 'Digital Dream Safaris',
      date: '2023-12-05',
      readTime: '7 min read',
      content: `
        <p>The Maasai people are one of Africa's most famous ethnic groups, known for their distinctive red clothing, intricate beadwork, and traditional way of life. Experiencing Maasai culture is a highlight of any visit to Tanzania.</p>
        
        <h2>Traditional Lifestyle</h2>
        <p>The Maasai are semi-nomadic pastoralists who have maintained their traditional way of life for centuries. They live in small villages called "bomas" and rely on cattle for their livelihood. Cattle are central to Maasai culture, representing wealth and status.</p>
        
        <h2>Visiting a Maasai Village</h2>
        <p>Many safari tours include visits to Maasai villages, where you can learn about their customs, see traditional homes (made from mud, sticks, and cow dung), and watch traditional dances. These visits provide income for the communities and help preserve their culture.</p>
        
        <h2>Traditional Dress</h2>
        <p>Maasai clothing is distinctive and colorful. Men wear red shukas (blankets) wrapped around their bodies, while women wear colorful beaded jewelry and clothing. The colors and patterns have specific meanings and are often related to age, marital status, and social position.</p>
        
        <h2>Beadwork</h2>
        <p>Maasai beadwork is world-renowned for its beauty and craftsmanship. The intricate patterns and colors carry deep cultural significance. You can purchase authentic Maasai beadwork as souvenirs, directly supporting the communities.</p>
        
        <h2>Traditional Dances</h2>
        <p>The Maasai are famous for their jumping dance, where warriors (moran) compete to see who can jump the highest. This dance is part of their coming-of-age ceremonies and celebrations. The rhythmic chanting and synchronized movements are mesmerizing to watch.</p>
        
        <h2>Respectful Tourism</h2>
        <p>When visiting Maasai communities, it's important to be respectful. Always ask permission before taking photos, respect their customs, and support community-run tourism initiatives. Many Maasai communities welcome visitors as a way to share their culture and generate income.</p>
        
        <p>Experiencing Maasai culture adds a rich cultural dimension to your Tanzanian adventure, providing insight into traditional African ways of life that have persisted for generations.</p>
      `
    },
    'safari-photography-tips': {
      id: 9,
      title: 'Photography Tips for Safari: Capturing Wildlife Moments',
      image: '/assets/imgs/destination/ruaha.webp',
      category: 'Travel Tips',
      author: 'Digital Dream Safaris',
      date: '2023-11-28',
      readTime: '9 min read',
      content: `
        <p>Capturing stunning wildlife photographs on safari requires more than just a good camera. Understanding animal behavior, lighting, and composition will help you create memorable images that tell the story of your African adventure.</p>
        
        <h2>Camera Equipment</h2>
        <p>While professional cameras produce the best results, modern smartphones can also capture great safari photos. If using a camera, consider:</p>
        <ul>
          <li>A telephoto lens (200mm or longer) for wildlife shots</li>
          <li>A wide-angle lens for landscapes</li>
          <li>Extra batteries and memory cards</li>
          <li>A bean bag or monopod for stability in vehicles</li>
        </ul>
        
        <h2>Understanding Light</h2>
        <p>The golden hours - early morning and late afternoon - provide the best light for photography. The soft, warm light creates beautiful colors and reduces harsh shadows. Avoid shooting in the harsh midday sun when possible.</p>
        
        <h2>Camera Settings</h2>
        <p>For wildlife photography, use these settings:</p>
        <ul>
          <li><strong>Aperture:</strong> f/5.6 to f/8 for good depth of field</li>
          <li><strong>Shutter Speed:</strong> 1/500s or faster for moving animals</li>
          <li><strong>ISO:</strong> Adjust based on lighting conditions</li>
          <li><strong>Focus:</strong> Use continuous autofocus for moving subjects</li>
        </ul>
        
        <h2>Composition Tips</h2>
        <p>Apply the rule of thirds, leave space for animals to move into, and try to capture animals at eye level. Include the environment to tell a story, not just close-up portraits. Look for interesting backgrounds and natural frames.</p>
        
        <h2>Patience is Key</h2>
        <p>Wildlife photography requires patience. Wait for the right moment - when an animal looks at the camera, displays interesting behavior, or is in perfect light. Sometimes the best shots come from waiting and observing.</p>
        
        <h2>Respect Wildlife</h2>
        <p>Never disturb animals for a photo. Use your zoom lens instead of getting too close. Remember that animal welfare comes before getting the perfect shot. Your guide will help position you safely for the best angles.</p>
        
        <h2>Practice Before You Go</h2>
        <p>Familiarize yourself with your camera settings before your safari. Practice photographing moving subjects and adjusting settings quickly. On safari, you'll need to react quickly to capture fleeting moments.</p>
        
        <p>Remember, the best camera is the one you have with you. Don't get so caught up in photography that you forget to enjoy the experience. Sometimes the best memories are the ones you experience with your own eyes.</p>
      `
    }
  };

  const post = blogPosts[slug];

  if (!post) {
    return (
      <div className="blog-detail-page">
        <div className="blog-not-found">
          <h2>Blog Post Not Found</h2>
          <p>The blog post you're looking for doesn't exist.</p>
          <Link to="/blogs" className="btn">Back to Blogs</Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="blog-detail-page">
      {/* Hero Image */}
      <section 
        className="blog-detail-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${post.image})`
        }}
      >
        <div className="blog-detail-hero-content">
          <div className="blog-detail-category">{post.category}</div>
          <h1 className="blog-detail-title">{post.title}</h1>
          <div className="blog-detail-meta">
            <div className="blog-detail-meta-item">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span>{post.author}</span>
            </div>
            <div className="blog-detail-meta-item">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>{formatDate(post.date)}</span>
            </div>
            <div className="blog-detail-meta-item">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>{post.readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="blog-detail-content">
        <div className="blog-detail-container">
          <article className="blog-detail-article">
            <div 
              className="blog-detail-body"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </article>

          {/* Share Section */}
          <div className="blog-detail-share">
            <h3 className="blog-share-title">Share this post</h3>
            <div className="blog-share-links">
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-share-link" 
                aria-label="Share on Facebook"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
                Facebook
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-share-link" 
                aria-label="Share on Twitter"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                </svg>
                Twitter
              </a>
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-share-link" 
                aria-label="Share on LinkedIn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
                LinkedIn
              </a>
            </div>
          </div>

          {/* Back to Blogs */}
          <div className="blog-detail-back">
            <Link to="/blogs" className="btn btn-secondary">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back to Blogs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BlogDetailPage;

